import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Clock, CheckCircle2, FileWarning, Gauge, Users, Wrench, Calendar, PauseCircle,
} from "lucide-react";
import { useTimeFormat } from "@/hooks/useTimeFormat";

function Kpi({ label, value, sub, Icon, accent = "text-blue-400", onClick, testid }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`surface rounded-xl p-4 md:p-5 min-w-0 ${clickable ? "cursor-pointer hover:bg-[#2A2F3A] transition-colors" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
      data-testid={testid || `kpi-${label}`}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{label}</div>
          <div className="font-display text-2xl md:text-3xl font-semibold mt-2 font-mono-stat">{value}</div>
          {sub && <div className="text-xs text-zinc-500 mt-1 truncate">{sub}</div>}
        </div>
        <div className={`h-9 w-9 shrink-0 rounded-lg bg-[#2A2F3A] flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

function OccupancyBar({ percent }) {
  const p = Math.min(percent, 150);
  const color = percent > 100 ? "bg-red-500" : percent >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-zinc-400">
        <span>0%</span><span className="font-mono-stat font-semibold text-zinc-200">{percent}%</span><span>150%</span>
      </div>
      <div className="h-2 rounded-full bg-[#171B24] border border-[#2E3241] mt-1 overflow-hidden">
        <div className={`${color} h-full transition-all`} style={{ width: `${(p / 150) * 100}%` }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const tf = useTimeFormat();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard", { params: { period } }).then((r) => setData(r.data));
  }, [period]);

  const drill = useMemo(() => (statusValue) => {
    if (!data) return;
    const qs = new URLSearchParams();
    if (statusValue) qs.set("status", statusValue);
    qs.set("period", period);
    if (data.period_from) qs.set("from", data.period_from);
    if (data.period_to) qs.set("to", data.period_to);
    nav(`/contracts?${qs.toString()}`);
  }, [data, period, nav]);

  if (!data) return <div className="text-zinc-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("dashboard.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1" data-testid="dashboard-heading">{t("dashboard.heading")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg overflow-hidden border border-[#2E3241] bg-[#171B24] text-xs"
               data-testid="dashboard-period-toggle">
            {["week", "month", "year"].map((p) => (
              <button key={p} type="button" onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 uppercase tracking-wider ${
                  period === p ? "bg-blue-500/20 text-blue-200" : "text-zinc-400 hover:text-zinc-200"
                }`}
                data-testid={`period-${p}`}>
                {t(`dashboard.period_${p}`)}
              </button>
            ))}
          </div>
          <div className="text-xs text-zinc-500 font-mono-stat">{data.period_from} → {data.period_to}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi label={t("dashboard.total")} value={data.total_contracts} Icon={TrendingUp}
             onClick={() => drill("")} testid="kpi-total" />
        <Kpi label={t("dashboard.new")} value={data.new_contracts} Icon={FileWarning} accent="text-zinc-300"
             onClick={() => drill("new")} testid="kpi-new" />
        <Kpi label={t("dashboard.in_progress")} value={data.in_progress_contracts} Icon={Clock} accent="text-amber-400"
             onClick={() => drill("in_progress")} testid="kpi-in-progress" />
        <Kpi label={t("dashboard.waiting")} value={data.waiting_contracts || 0} Icon={PauseCircle} accent="text-orange-400"
             onClick={() => drill("waiting_parts|waiting_customer")} testid="kpi-waiting" />
        <Kpi label={t("dashboard.finished")} value={data.finished_contracts} Icon={CheckCircle2} accent="text-emerald-400"
             onClick={() => drill("finished")} testid="kpi-finished" />
        <Kpi label={t("dashboard.billed")} value={data.billed_contracts} Icon={CheckCircle2} accent="text-emerald-400"
             onClick={() => drill("billed")} testid="kpi-billed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="surface rounded-xl p-6 lg:col-span-2">
          <div className="flex items-center gap-2 text-zinc-300">
            <Gauge className="h-4 w-4 text-blue-400" />
            <span className="text-xs uppercase tracking-widest text-zinc-500">{t("dashboard.occupancy")}</span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">{t("dashboard.capacity")}</div>
              <div className="font-display text-2xl font-mono-stat mt-1">{tf.format(data.total_capacity_aw || 0)}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">{t("dashboard.planned_aw")}</div>
              <div className="font-display text-2xl font-mono-stat mt-1" data-testid="dashboard-planned-worktime">{tf.format(data.planned_aw_today || 0)}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">{t("dashboard.occupancy")}</div>
              <div className="font-display text-2xl font-mono-stat mt-1" data-testid="occupancy-percent">{data.occupancy_percent}%</div>
            </div>
          </div>
          <div className="mt-5">
            <OccupancyBar percent={data.occupancy_percent} />
          </div>
        </div>

        <div className="surface rounded-xl p-6">
          <div className="flex items-center gap-2 text-zinc-300">
            <Users className="h-4 w-4 text-red-400" />
            <span className="text-xs uppercase tracking-widest text-zinc-500">{t("dashboard.overloaded")}</span>
          </div>
          {data.overloaded_employees.length === 0 ? (
            <p className="text-sm text-zinc-500 mt-4">—</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.overloaded_employees.map((e) => (
                <li key={e.id} className="flex items-center justify-between rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
                  <span className="text-sm">{e.name}</span>
                  <span className="text-xs font-mono-stat text-red-300">{tf.format(e.assigned_aw)} / {tf.format(e.capacity_aw)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="surface rounded-xl p-6">
        <div className="flex items-center gap-2 text-zinc-300">
          <Calendar className="h-4 w-4 text-blue-400" />
          <span className="text-xs uppercase tracking-widest text-zinc-500">{t("dashboard.today")}</span>
        </div>
        {data.today_contracts.length === 0 ? (
          <p className="text-sm text-zinc-500 mt-4">{t("common.empty")}</p>
        ) : (
          <ul className="mt-4 divide-y divide-[#2E3241]">
            {data.today_contracts.map((c) => (
              <li
                key={c.id}
                className="py-3 flex items-center justify-between cursor-pointer hover:bg-[#2A2F3A] rounded-lg px-2 -mx-2"
                onClick={() => nav(`/contracts/${c.id}`)}
                data-testid={`today-contract-${c.number}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Wrench className="h-4 w-4 text-zinc-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm truncate">{c.title || c.number}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.number} · {c.planned_time || "—"}</div>
                  </div>
                </div>
                <StatusBadge status={c.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
